import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/endpoints';

/**
 * Hook to upload a file and start ingestion
 */
export const useUploadFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.ingestion.upload(formData, onProgress);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
    },
  });
};

/**
 * Hook to fetch list of ingestion jobs
 */
export const useIngestionJobs = (params?: {
  page?: number;
  limit?: number;
  status?: string;
}) => {
  return useQuery({
    queryKey: ['ingestion-jobs', params],
    queryFn: async () => {
      const response = await api.ingestion.listJobs(params);
      return response.data;
    },
    refetchInterval: 5000, // Refetch every 5 seconds to track progress
  });
};

/**
 * Hook to fetch ingestion job status
 */
export const useIngestionJob = (jobId: string) => {
  return useQuery({
    queryKey: ['ingestion-jobs', jobId],
    queryFn: async () => {
      const response = await api.ingestion.status(jobId);
      return response.data;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      // Refetch every 2 seconds if job is in progress
      const data = query.state.data;
      if (data?.status === 'processing' || data?.status === 'pending') {
        return 2000;
      }
      return false; // Don't refetch if job is complete
    },
  });
};

/**
 * Hook to validate uploaded data
 */
export const useValidateIngestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, config }: { jobId: string; config?: any }) => {
      const response = await api.ingestion.validate(jobId, config);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs', variables.jobId] });
    },
  });
};

/**
 * Hook to extract and validate schema
 */
export const useSchemaIngestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, config }: { jobId: string; config?: any }) => {
      const response = await api.ingestion.schema(jobId, config);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs', variables.jobId] });
    },
  });
};

/**
 * Hook to register in catalog
 */
export const useCatalogIngestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, metadata }: { jobId: string; metadata: any }) => {
      const response = await api.ingestion.catalog(jobId, metadata);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs', variables.jobId] });
    },
  });
};

/**
 * Hook to run quality checks
 */
export const useQualityIngestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, rules }: { jobId: string; rules?: any }) => {
      const response = await api.ingestion.quality(jobId, rules);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs', variables.jobId] });
    },
  });
};

/**
 * Hook to finalize ingestion
 */
export const useFinalizeIngestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const response = await api.ingestion.finalize(jobId);
      return response.data;
    },
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs', jobId] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};

/**
 * Hook to cancel ingestion job
 */
export const useCancelIngestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const response = await api.ingestion.cancel(jobId);
      return response.data;
    },
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs', jobId] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
    },
  });
};
